import React, { useState, useEffect, useMemo } from 'react';
import {
  KTSVG,
  SimpleAlert,
  userHasRole,
} from '../../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import { shallowEqual, useSelector } from 'react-redux';
import { UserModel } from '../../../../modules/auth/models/UserModel';
import { RootState } from '../../../../../setup';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { statusSelectModalStyles } from '../../../inventario/entrabas_salidas/models/statusSelectStyles';
import Select from 'react-select';
import { getSelectEmpresas } from '../../../../service/empresas/Empresas';
import {
  getComisionesPendientes,
  payCommission,
} from '../../../../service/pagos/comisiones';
import { getAllSelect } from '../../../../service/distribuidores/Distribuidores';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
  handleRefresh: (updateGrid: boolean) => void;
};

interface SelectOption {
  label: string;
  value: string;
  id?: string;
  type?: { code: string };
  tipo?: string;
  partner?: { code: string };
  account?: {
    typeCommission: { code: string };
  };
  first_name?: string;
  father_last_name?: string;
  bank_name?: string;
  interbank_key?: string;
  active?: boolean;
}

const getPayCommissionSchema = (isAdmin: boolean) =>
  Yup.object().shape({
    empresa: Yup.mixed().required('Seleccione un cliente.'),
    ...(isAdmin && {
      cliente: Yup.mixed().required('Seleccione un tipo de cliente.'),
    }),
  });

const currentDate = new Date();
const fechaMaxima = new Date(currentDate);
fechaMaxima.setFullYear(currentDate.getFullYear() - 18);

const PagarComisionModal: React.FC<Props> = ({
  show,
  handleClose,
  handleRefresh,
}) => {
  const userSesion: UserModel = useSelector<RootState>(
    ({ auth }) => auth.user,
    shallowEqual,
  ) as UserModel;
  const { roles: userRoles } = userSesion;

  const isAdmin = useMemo(
    () => userHasRole(['ADMIN_MEXABLOCK'], userRoles),
    [userRoles],
  );

  const [loading, setLoading] = useState(false);
  const [initValues] = useState<{
    is_output: boolean;
    package_id: string;
    motive_id: number;
    original_location: string;
    current_location: string;
    detail: string;
    stock: number[];
    cliente: string;
    empresa: string;
  }>({
    is_output: true,
    package_id: '',
    motive_id: 0,
    original_location: '',
    current_location: '',
    detail: '',
    stock: [],
    cliente: '',
    empresa: '',
  });

  const [empresas, setEmpresas] = useState<SelectOption[]>([]);
  const [distribuidores, setDistribuidores] = useState<SelectOption[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [radioSelection, setRadioSelection] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState<any>(null);
  const [comisionesPendientes, setComisionesPendientes] = useState<any[]>([]);
  const [fechaInicio, setFechaInicio] = useState<string>('');
  const [fechaFin, setFechaFin] = useState<string>('');
  const [montoPagar, setMontoPagar] = useState<string>('0.00');
  const [isTyping, setIsTyping] = useState<boolean>(false);

  const typeClient = [
    {
      label: 'Empresa',
      value: 'empresa',
    },
    {
      label: 'Socio',
      value: 'socio',
    },
    {
      label: 'Distribuidor',
      value: 'distribuidor',
    },
  ];

  const validationSchema = useMemo(
    () => getPayCommissionSchema(isAdmin),
    [isAdmin],
  );

  const formPackMovement = useFormik({
    initialValues: initValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, setFieldError }) => {
      setLoading(true);

      if (!selectedEmpresa || !selectedEmpresa.id) {
        SimpleAlert('Debe seleccionar un cliente', 3000, 'error');
        setSubmitting(false);
        setLoading(false);
        return;
      }

      if (selectedItems.length === 0) {
        SimpleAlert(
          'Debe seleccionar al menos una comisión a pagar',
          3000,
          'error',
        );
        setSubmitting(false);
        setLoading(false);
        return;
      }

      const montoNumerico = parseFloat(montoPagar || '0');
      if (montoNumerico <= 0) {
        SimpleAlert('El monto a pagar debe ser mayor a $0.00', 3000, 'error');
        setSubmitting(false);
        setLoading(false);
        return;
      }

      const commissionIds = selectedItems.map((id) => Number(id));

      const payload = {
        companyId: Number(selectedEmpresa.id),
        commissionIds: commissionIds,
      };

      payCommission(payload)
        .then((res) => {
          SimpleAlert(res.message, 3000, 'success');
          setLoading(false);
          handleRefresh(true);
          onClose();
        })
        .catch((err) => {
          let resMessageToast: string = 'Ocurrió un error inesperado.';
          if (err.response && err.response.data) {
            const { message, details = null } = err.response.data;
            resMessageToast = `${message}`;
            if (details && Array.isArray(details) && details.length) {
              resMessageToast = `${details[0].message}`;
            }
          } else if (err.message) {
            resMessageToast = err.message;
          }
          setLoading(false);
          setSubmitting(false);
          SimpleAlert(resMessageToast, 3000, 'error');
        });
    },
  });

  const empresasOptions = useMemo(
    () =>
      empresas.filter(
        (emp) => emp.type?.code === radioSelection && emp.tipo === 'EFF',
      ),
    [empresas, radioSelection],
  );

  const distribuidoresAdminOptions = useMemo(
    () =>
      distribuidores.filter(
        (dist) =>
          // dist.active === true &&
          dist.account?.typeCommission?.code === 'EFF',
      ),
    [distribuidores],
  );

  const distribuidoresOptions = useMemo(() => {
    const filtered = distribuidores.filter(
      (dist) => dist.account?.typeCommission?.code === 'EFF',
    );
    return filtered;
  }, [distribuidores]);

  const toggleSelection = (id: string) => {
    const newSelectedItems = selectedItems.includes(id)
      ? selectedItems.filter((item) => item !== id)
      : [...selectedItems, id];

    setSelectedItems(newSelectedItems);

    const selectedTotal = comisionesPendientes
      .filter((item) => newSelectedItems.includes(String(item.id)))
      .reduce((sum, item) => sum + parseFloat(item.commission_amount), 0);

    setMontoPagar(selectedTotal > 0 ? selectedTotal.toFixed(2) : '0.00');
  };

  const handleSelectAll = () => {
    if (selectedItems.length === comisionesPendientes.length) {
      setSelectedItems([]);
      setMontoPagar('0.00');
    } else {
      const allIds = comisionesPendientes.map((item) => String(item.id));
      setSelectedItems(allIds);
      const total = comisionesPendientes.reduce(
        (sum, item) => sum + parseFloat(item.commission_amount),
        0,
      );
      setMontoPagar(total.toFixed(2));
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getOperationTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      PROVISION_CASH: 'Aprovisionamiento Efectivo',
      PROVISION_VIRTUAL: 'Aprovisionamiento Virtual',
      RECHARGE_CASH: 'Recarga Efectivo',
      RECHARGE_VIRTUAL: 'Recarga Virtual',
    };
    return types[type] || type;
  };

  const totalAcumulado = comisionesPendientes.reduce(
    (sum, item) => sum + parseFloat(item.commission_amount),
    0,
  );

  const montoRestante = totalAcumulado - parseFloat(montoPagar || '0');

  const selectItemsForAmount = (targetAmount: number) => {
    if (targetAmount <= 0) {
      setSelectedItems([]);
      return;
    }

    const descendingSorted = [...comisionesPendientes].sort(
      (a, b) =>
        parseFloat(b.commission_amount) - parseFloat(a.commission_amount),
    );

    let currentSum = 0;
    const selected: string[] = [];

    for (const commission of descendingSorted) {
      const amount = parseFloat(commission.commission_amount);
      if (currentSum + amount <= targetAmount + 0.01) {
        selected.push(String(commission.id));
        currentSum += amount;
      }
    }

    if (selected.length === 0) {
      const sortedAsc = [...comisionesPendientes].sort(
        (a, b) =>
          parseFloat(a.commission_amount) - parseFloat(b.commission_amount),
      );
      for (const commission of sortedAsc) {
        const amount = parseFloat(commission.commission_amount);
        if (amount <= targetAmount + 0.01) {
          selected.push(String(commission.id));
          break;
        }
      }
    }

    setSelectedItems(selected);
  };

  const selectItemsForAmountAndAdjust = (targetAmount: number) => {
    if (targetAmount <= 0) {
      setSelectedItems([]);
      setMontoPagar('0.00');
      return;
    }

    const descendingSorted = [...comisionesPendientes].sort(
      (a, b) =>
        parseFloat(b.commission_amount) - parseFloat(a.commission_amount),
    );

    let currentSum = 0;
    const selected: string[] = [];

    for (const commission of descendingSorted) {
      const amount = parseFloat(commission.commission_amount);
      if (currentSum + amount <= targetAmount + 0.01) {
        selected.push(String(commission.id));
        currentSum += amount;
      }
    }

    if (selected.length === 0) {
      const sortedAsc = [...comisionesPendientes].sort(
        (a, b) =>
          parseFloat(a.commission_amount) - parseFloat(b.commission_amount),
      );
      for (const commission of sortedAsc) {
        const amount = parseFloat(commission.commission_amount);
        if (amount <= targetAmount + 0.01) {
          selected.push(String(commission.id));
          currentSum = amount;
          break;
        }
      }
    }

    setSelectedItems(selected);

    const actualSum = comisionesPendientes
      .filter((item) => selected.includes(String(item.id)))
      .reduce((sum, item) => sum + parseFloat(item.commission_amount), 0);

    setMontoPagar(actualSum > 0 ? actualSum.toFixed(2) : '0.00');
  };

  const handleMontoPagarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      setMontoPagar('0.00');
      setSelectedItems([]);
      return;
    }

    if (!/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    setMontoPagar(value);
    setIsTyping(true);
  };

  const handleMontoPagarBlur = () => {
    const numericValue = parseFloat(montoPagar || '0');

    setIsTyping(false);

    if (numericValue > totalAcumulado) {
      const capped = totalAcumulado.toFixed(2);
      setMontoPagar(capped);
      selectItemsForAmount(totalAcumulado);
    } else if (numericValue > 0) {
      selectItemsForAmountAndAdjust(numericValue);
    } else {
      setMontoPagar('0.00');
      setSelectedItems([]);
    }
  };

  const fetchComisionesPendientes = async (
    companyId: string | number,
    startDate?: string,
    endDate?: string,
  ) => {
    try {
      const filters: any = {
        company_id: companyId,
      };

      if (startDate) {
        filters.start_date = startDate;
      }
      if (endDate) {
        filters.end_date = endDate;
      }

      const response = await getComisionesPendientes(filters);

      if (response && Array.isArray(response)) {
        setComisionesPendientes(response);
        const allIds = response.map((item: any) => String(item.id));
        setSelectedItems(allIds);
        const total = response.reduce(
          (sum: number, item: any) => sum + parseFloat(item.commission_amount),
          0,
        );
        setMontoPagar(total.toFixed(2));
      } else {
        setComisionesPendientes([]);
        setSelectedItems([]);
        setMontoPagar('0.00');
      }
    } catch (error) {
      console.error('Error al obtener comisiones pendientes:', error);
      setComisionesPendientes([]);
      setSelectedItems([]);
    }
  };

  const handleEmpresaChange = async (value: SelectOption | null) => {
    formPackMovement.setFieldValue('empresa', value);
    setSelectedEmpresa(value);

    if (value && value.id) {
      // Reset fechas al cambiar de empresa
      setFechaInicio('');
      setFechaFin('');
      // Fetch sin filtros de fecha
      await fetchComisionesPendientes(value.id);
    } else {
      setComisionesPendientes([]);
      setSelectedEmpresa(null);
      setSelectedItems([]);
      setMontoPagar('0.00');
      setFechaInicio('');
      setFechaFin('');
    }
  };

  const handleTypeClientChange = (selectedOption: SelectOption | null) => {
    if (!selectedOption) return;

    formPackMovement.setFieldValue('cliente', selectedOption);
    formPackMovement.setFieldValue('empresa', null);
    setSelectedEmpresa(null);
    setComisionesPendientes([]);
    setSelectedItems([]);
    setMontoPagar('0.00');
    setFechaInicio('');
    setFechaFin('');

    const clientType = selectedOption.value;
    if (clientType === 'socio') {
      setRadioSelection('SOC');
    } else if (clientType === 'empresa') {
      setRadioSelection('EMP');
    } else if (clientType === 'distribuidor') {
      setRadioSelection('DIST');
    }
  };

  const handleFechaInicioChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newFechaInicio = e.target.value;

    setFechaInicio(newFechaInicio);

    if (!newFechaInicio) {
      setFechaFin('');
      return;
    }

    if (fechaFin && newFechaInicio > fechaFin) {
      setFechaFin('');
    }

    if (selectedEmpresa && selectedEmpresa.id) {
      await fetchComisionesPendientes(
        selectedEmpresa.id,
        newFechaInicio,
        fechaFin,
      );
    }
  };

  const handleFechaFinChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newFechaFin = e.target.value;
    setFechaFin(newFechaFin);

    if (selectedEmpresa && selectedEmpresa.id) {
      await fetchComisionesPendientes(
        selectedEmpresa.id,
        fechaInicio,
        newFechaFin,
      );
    }
  };

  const onClose = () => {
    formPackMovement.resetForm();
    setSelectedItems([]);
    setSelectedEmpresa(null);
    setRadioSelection('');
    setFechaInicio('');
    setFechaFin('');
    setComisionesPendientes([]);
    setMontoPagar('0.00');
    handleClose(false);
  };

  const fetchInitialData = async () => {
    try {
      const distribuidoresData = await getAllSelect({});
      setDistribuidores(distribuidoresData || []);

      if (isAdmin) {
        const empresasData = await getSelectEmpresas();
        setEmpresas(empresasData || []);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      SimpleAlert('Error al cargar los datos iniciales', 3000, 'error');
    }
  };
  useEffect(() => {
    if (show) {
      fetchInitialData();
    }
  }, [show]);

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      backdrop="static"
      keyboard={false}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={onClose}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body scroll-y mx-10">
          <div className="text-center mb-13">
            <h1 className="mb-3">Pagar comisión</h1>
            <div className="text-muted fw-semibold fs-5 lh-sm mx-auto w-75">
              Captura el monto de comisión a pagar o selecciona los conceptos
              directamente.
            </div>
          </div>
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formPackMovement.handleSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          >
            <div className="container-fluid px-0">
              <div className="row g-6">
                {/* Detalles de devolución */}
                <div className="col-6 p-5 rounded">
                  <span className="fw-bolder fs-5 text-gray-600">
                    Selección de cliente
                  </span>
                  <div className="separator border-2 my-4" />
                  <div className="row mb-4">
                    {isAdmin ? (
                      <>
                        {/* Tipo de cliente - Solo admin */}
                        <div className="mb-4 col-4">
                          <label className="fw-bold fs-6 mb-2 required">
                            Tipo de cliente
                          </label>
                          <Select
                            options={typeClient}
                            placeholder={'Seleccione'}
                            onChange={handleTypeClientChange}
                            value={formPackMovement.values.cliente}
                            noOptionsMessage={() => (
                              <span>No hay registros</span>
                            )}
                            menuPortalTarget={document.body}
                            styles={statusSelectModalStyles}
                          />
                        </div>

                        {/* Cliente/Distribuidor - Admin */}
                        <div className="mb-4 col-8">
                          <label className="required fw-bold fs-6 mb-2">
                            {radioSelection === 'DIST'
                              ? 'Distribuidor'
                              : 'Cliente'}
                          </label>
                          {radioSelection === 'DIST' ? (
                            <Select
                              options={distribuidoresAdminOptions}
                              placeholder={'Seleccione'}
                              onChange={handleEmpresaChange}
                              value={formPackMovement.values.empresa}
                              noOptionsMessage={() => (
                                <span>No hay registros</span>
                              )}
                              menuPortalTarget={document.body}
                              styles={statusSelectModalStyles}
                            />
                          ) : (
                            <Select
                              options={empresasOptions}
                              placeholder={'Seleccione'}
                              onChange={handleEmpresaChange}
                              value={formPackMovement.values.empresa}
                              noOptionsMessage={() => (
                                <span>No hay registros</span>
                              )}
                              menuPortalTarget={document.body}
                              styles={statusSelectModalStyles}
                              isDisabled={!radioSelection}
                            />
                          )}
                        </div>
                      </>
                    ) : (
                      /* Distribuidor - Solo no-admin */
                      <div className="mb-4 col-12">
                        <label className="required fw-bold fs-6 mb-2">
                          Distribuidor
                        </label>
                        <Select
                          options={distribuidoresOptions}
                          placeholder={'Seleccione'}
                          onChange={handleEmpresaChange}
                          value={formPackMovement.values.empresa}
                          noOptionsMessage={() => <span>No hay registros</span>}
                          menuPortalTarget={document.body}
                          styles={statusSelectModalStyles}
                        />
                      </div>
                    )}
                  </div>

                  {/* Usuario y Fecha */}
                  <div className="mb-4">
                    <span className="fw-bolder fs-5 text-gray-600">
                      Periodo
                    </span>
                    <div className="separator border-2 my-4" />
                    <div className="row">
                      <div className="mb-4 col-6">
                        <label className="fw-bold fs-6 mb-2">
                          Fecha inicio
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={fechaInicio}
                          onChange={handleFechaInicioChange}
                          max={fechaFin || undefined}
                          disabled={!selectedEmpresa}
                          onKeyDown={(e) => e.preventDefault()}
                          onPaste={(e) => e.preventDefault()}
                        />
                      </div>
                      <div className="mb-4 col-6">
                        <label className="fw-bold fs-6 mb-2">Fecha fin</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fechaFin}
                          onChange={handleFechaFinChange}
                          disabled={!fechaInicio}
                          min={fechaInicio || undefined}
                          onKeyDown={(e) => e.preventDefault()}
                          onPaste={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Datos de pago */}
                  <div className="mb-4">
                    <span className="fw-bolder fs-5 text-gray-600">
                      Datos de pago
                    </span>
                    <div className="separator border-2 my-4" />
                    <div className="mb-4">
                      <label className="fw-bold fs-6 mb-2">
                        Titular de la cuenta
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedEmpresa
                            ? `${selectedEmpresa.first_name || ''} ${selectedEmpresa.father_last_name || ''
                              }`.trim()
                            : ''
                        }
                        disabled
                      />
                    </div>
                    <div className="mb-4">
                      <label className="fw-bold fs-6 mb-2">Banco</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedEmpresa?.bank_name || ''}
                        disabled
                      />
                    </div>
                    <div className="mb-4">
                      <label className="fw-bold fs-6 mb-2">
                        CLABE interbancaria
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedEmpresa?.interbank_key || ''}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Selección de productos */}
                <div className="col-6 p-5 rounded">
                  <span className="fw-bolder fs-5 text-gray-600">
                    Resumen de comisiones
                  </span>
                  <div className="separator border-2 my-4" />

                  {/* Buscador */}
                  <div className="row">
                    <div className="mb-4 col-6">
                      <label className="fw-bold fs-6 mb-2 required">
                        Monto a pagar
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control"
                          value={montoPagar}
                          onChange={handleMontoPagarChange}
                          onBlur={handleMontoPagarBlur}
                          placeholder="0.00"
                          disabled={
                            !selectedEmpresa ||
                            comisionesPendientes.length === 0
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMontoPagarBlur();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-4 col">
                      <label className="fw-bold fs-6 mb-2">
                        Monto restante
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control"
                          value={montoRestante.toFixed(2)}
                          disabled
                        />
                      </div>
                    </div>
                    {parseFloat(montoPagar || '0') > totalAcumulado && (
                      <div className="text-danger mb-2 fs-7">
                        El monto no puede exceder el total acumulado
                      </div>
                    )}
                  </div>

                  {/* Tabla de productos */}
                  <div
                    className="table-responsive"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    <table className="table align-middle table-row-dashed fs-6 gy-3">
                      <thead>
                        <tr className="text-start text-muted fw-bolder fs-7 gs-0 bg-light">
                          <th scope="col" className="ps-4">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              onChange={handleSelectAll}
                              checked={
                                selectedItems.length ===
                                comisionesPendientes.length &&
                                comisionesPendientes.length > 0
                              }
                            />
                          </th>
                          <th scope="col">Fecha</th>
                          <th scope="col">Origen</th>
                          <th scope="col">Comisión</th>
                        </tr>
                      </thead>

                      <tbody>
                        {comisionesPendientes.length > 0 ? (
                          comisionesPendientes.map((item) => (
                            <tr key={item.id}>
                              <td className="ps-4">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedItems.includes(
                                    String(item.id),
                                  )}
                                  onChange={() =>
                                    toggleSelection(String(item.id))
                                  }
                                />
                              </td>
                              <td>{formatDate(item.createdAt)}</td>
                              <td>
                                {getOperationTypeLabel(item.operation_type)}
                              </td>
                              <td className="text-primary">
                                ${parseFloat(item.commission_amount).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="text-center text-muted">
                              {selectedEmpresa
                                ? 'No hay comisiones pendientes'
                                : 'Seleccione un cliente para ver comisiones'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="fw-bold border-top">
                          <td colSpan={3} className="text-end pe-3 fw-bolder">
                            Total acumulado:
                          </td>
                          <td className="text-start text-primary">
                            ${totalAcumulado.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="d-flex justify-content-end gap-3 mt-5 pb-5">
              <button
                type="button"
                className="btn btn-light"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading || selectedItems.length === 0 || !selectedEmpresa
                }
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  'Pagar comisión'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export { PagarComisionModal };
